import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setupMfaApi, verifyMfaSetupApi } from "../../api";
import styles from "./Login.module.css";

interface MfaSetupProps {
    setupToken: string;
    onSuccess: (finalToken: string) => void;
}

export const MfaSetup: React.FC<MfaSetupProps> = ({ setupToken, onSuccess }) => {
    const { t } = useTranslation();
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [totpCode, setTotpCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const handleEnableMfa = async () => {
            setError("");
            setLoading(true);
            try {
                const qrBlob = await setupMfaApi(setupToken);
                setQrCodeUrl(URL.createObjectURL(qrBlob));
            } catch (err: any) {
                setError(err.message || "Failed to start MFA setup.");
            } finally {
                setLoading(false);
            }
        };

        handleEnableMfa();
    }, [setupToken]);

    const handleVerifyMfa = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (totpCode) {
            try {
                const response = await verifyMfaSetupApi(setupToken, totpCode);
                if (response.token) {
                    onSuccess(response.token);
                } else {
                    setError("MFA verification succeeded, but no token was returned. Please try logging in again.");
                }
            } catch (err: any) {
                setError(err.message || "Invalid code.");
            }
        }
    };

    if (loading) {
        return <div>Loading MFA setup...</div>;
    }

    return (
        <div className={styles.localAuthContainer}>
            <h3 className={styles.localAuthTitle}>{t("profile.setupTitle")}</h3>
            <p>{t("profile.scanInstruction")}</p>

            {qrCodeUrl && <img src={qrCodeUrl} alt={t("profile.qrCodeAlt")} style={{ margin: "20px 0" }} />}

            <form onSubmit={handleVerifyMfa} className={styles.form}>
                <p>{t("profile.verifyInstruction")}</p>
                <div className={styles.inputGroup}>
                    <label htmlFor="totp-code">{t("loginComponent.mfaInputLabel")}</label>
                    <input
                        id="totp-code"
                        type="text"
                        value={totpCode}
                        onChange={e => setTotpCode(e.target.value)}
                        placeholder={t("profile.totpPlaceholder")}
                        required
                        className={styles.input}
                    />
                </div>
                {error && <p className={styles.error}>{error}</p>}
                <button type="submit" className={styles.loginButton}>
                    {t("profile.verifyAndEnableButton")}
                </button>
            </form>
        </div>
    );
};
